import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PhoneCall, MessageCircleMore, PenTool, Footprints } from 'lucide-react';
import { toast } from '@sakura-grp/ui/sonner';
import { triggerHaptic } from '~/lib/haptics';
import type { CustomerDetailData } from '~/types/customer-detail.types';
import type { ActivityType } from '~/lib/api/activities';
import { useCreateActivity } from '~/hooks/useActivities';
import { useImeSafeSearch } from '@/hooks/useImeSafeSearch';
import { useHostessStore, getCurrentStoreId } from '@/stores/hostess';

interface BranchTabContentProps {
  data: CustomerDetailData;
}

export const BranchTabContent = ({ data }: BranchTabContentProps) => {
  const createActivity = useCreateActivity();
  const { hostess } = useHostessStore();
  const [appliedSearchText, setAppliedSearchText] = useState('');
  const search = useImeSafeSearch({onApply: setAppliedSearchText});
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(
    () => new Set(data.stores.map(s => s.storeId))
  );

  const toggleStore = (storeId: string) => {
    setSelectedStoreIds(prev => {
      const next = new Set(prev);
      if (next.has(storeId)) {
        next.delete(storeId);
      } else {
        next.add(storeId);
      }
      return next;
    });
  };

  const filteredBranches = useMemo(() => {
    if (!data.branches) return [];
    return data.branches.filter(branch => {
      // テキスト検索（名前・かな・会社名）
      if (appliedSearchText) {
        const q = appliedSearchText.toLowerCase();
        const match = branch.name.toLowerCase().includes(q) ||
          branch.kana.toLowerCase().includes(q) ||
          branch.company.toLowerCase().includes(q) ||
          branch.latestVisit.includes(q);
        if (!match) return false;
      }
      // 店舗フィルター
      if (selectedStoreIds.size < data.stores.length && branch.storeIds) {
        return branch.storeIds.some(sid => selectedStoreIds.has(sid));
      }
      return true;
    });
  }, [data.branches, data.stores.length, appliedSearchText, selectedStoreIds]);

  const handleActivityClick = (customerId: string, activityType: ActivityType) => {
    if (!hostess) {
      toast.error('ログイン情報を取得できませんでした');
      return;
    }
    const storeId = getCurrentStoreId(hostess);
    if (!storeId) {
      toast.error('店舗情報がありません');
      return;
    }
    triggerHaptic("light");
    createActivity.mutate({
      store_id: storeId,
      customer_id: customerId,
      hostess_id: hostess.id,
      activity_type: activityType,
    });
  };
  const getSalesStatusColor = (status: string) => {
    return status === 'good'
      ? { bg: 'bg-green-400', text: 'text-green-500' }
      : { bg: 'bg-red-400', text: 'text-red-500' };
  };

  const getSalesStatusLabel = (status: string) => {
    return status === 'good' ? '営業良好' : '営業不足';
  };

  return (
    <div className="border-t">
      {/* ツールバー */}
      <div className="px-lg gap-md h-(--h-list) flex items-center border-b">
        {/* 検索フィールド */}
        <div className="flex grow overflow-hidden rounded-lg border">
          <input
            data-slot="input"
            className="h-xl bg-input/10 text-md shadow-xs selection:bg-primary selection:text-primary-foreground file:text-foreground placeholder:text-muted-foreground/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 flex w-full min-w-[4rem] shrink px-3 py-1 outline-none transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            placeholder="名前・会社名で検索"
            {...search.inputProps}
          />
          <button
            data-slot="popover-trigger"
            className="h-xl bg-background p-md shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 inline-flex items-center justify-start whitespace-nowrap border-s text-left text-xs font-normal tracking-tight outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50"
            type="button"
            onClick={() => { search.setDraftValue(''); search.applyNow(''); }}
          >
            {search.draftValue ? 'クリア' : '絞る'}
          </button>
        </div>

        {/* 店舗フィルター */}
        <div className="!divide-(--border) flex flex-none items-center divide-x rounded-lg border bg-white text-xs font-bold">
          {data.stores.map((store, index) => {
            const isChecked = selectedStoreIds.has(store.storeId);
            return (
              <div key={store.storeShortName} className="gap-xs flex items-center justify-center px-[.45rem]">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isChecked}
                  data-state={isChecked ? 'checked' : 'unchecked'}
                  data-slot="checkbox"
                  className="size-(--bigger-sm) border-input shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground peer rounded-sm border bg-white outline-none transition-shadow focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
                  id={`select-${index + 1}`}
                  onClick={() => toggleStore(store.storeId)}
                >
                  {isChecked && (
                    <span
                      data-state="checked"
                      data-slot="checkbox-indicator"
                      className="flex items-center justify-center text-current transition-none"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-check size-md"
                        aria-hidden="true"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                    </span>
                  )}
                </button>
                <label
                  data-slot="label"
                  className="h-xl gap-md flex cursor-pointer select-none items-center text-xs font-bold leading-none tracking-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-50 group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50"
                  htmlFor={`select-${index + 1}`}
                >
                  {store.storeShortName}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* 枝客リスト */}
      <ul className="divide-y divide-stone-200">
        {filteredBranches.length > 0 ? (
          filteredBranches.map((branch) => (
            <li key={branch.id} className="gap-sm px-lg h-(--h-list) relative flex flex-col justify-center">
              {/* 上段: 登録日、枝客ラベル、営業ステータス、活動カウント */}
              <div className="gap-md flex items-center">
                <span className="text-sm tracking-normal">{branch.latestVisit}</span>
                <strong className="py-sm bg-secondary text-primary text-2xs absolute end-0 top-0 rounded-bl-lg border-b border-s px-[.7em] font-bold leading-snug tracking-tight">
                  {/* 店舗名は簡略化のため省略 */}
                </strong>
                <span className="text-sm">枝客</span>
                {(() => {
                  const statusColor = getSalesStatusColor(branch.salesStatus);
                  const statusLabel = getSalesStatusLabel(branch.salesStatus);
                  return (
                    <span className="gap-xs px-sm flex items-center">
                      <span className={`inline-block size-[1em] rounded-full ${statusColor.bg}`}></span>
                      <span className={`text-xs font-bold ${statusColor.text}`}>{statusLabel}</span>
                    </span>
                  );
                })()}
                {/* 営業活動カウント */}
                <ul className="gap-sm flex text-xs">
                  <li>
                    <PhoneCall className="me-xs size-(--bigger-md) text-green-600" />
                    {branch.activityCounts.phone}
                  </li>
                  <li>
                    <MessageCircleMore className="me-xs size-(--bigger-md) text-green-600" />
                    {branch.activityCounts.message}
                  </li>
                  <li>
                    <PenTool className="me-xs size-(--bigger-md) text-green-600" />
                    {branch.activityCounts.letter}
                  </li>
                  <li>
                    <Footprints className="me-xs size-(--bigger-md) text-green-600" />
                    {branch.activityCounts.visit}
                  </li>
                </ul>
              </div>

              {/* 下段: 枝客情報とアクションボタン */}
              <div className="flex items-center justify-between">
                {/* 枝客情報 */}
                <div className="gap-sm flex items-center">
                  <div className="space-y-xs float-none w-[2.45rem] text-center text-[2rem] leading-none">
                    <div>🪾</div>
                    <div className="text-sm text-brand">{branch.points}pt</div>
                  </div>
                  <div className="space-y-sm">
                    <Link to={`/customers/${branch.id}`} className="block">
                      <h3 className="text-2xl leading-none text-gray-800">{branch.name}</h3>
                    </Link>
                    {branch.company && <span className="block text-xs font-normal tracking-tight">( {branch.company} )</span>}
                  </div>
                </div>

                {/* アクションボタン */}
                <div className="gap-md flex items-center">
                  <button
                    type="button"
                    onClick={() => handleActivityClick(branch.id, 'phone')}
                    disabled={createActivity.isPending || branch.restrictions.phone}
                    className="grid place-items-center transition-transform active:scale-95 disabled:opacity-40 disabled:active:scale-100"
                  >
                    <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                      <PhoneCall className="size-[1.35rem]" />
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleActivityClick(branch.id, 'message')}
                    disabled={createActivity.isPending || branch.restrictions.message}
                    className="grid place-items-center transition-transform active:scale-95 disabled:opacity-40 disabled:active:scale-100"
                  >
                    <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                      <MessageCircleMore className="size-[1.35rem]" />
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleActivityClick(branch.id, 'letter')}
                    disabled={createActivity.isPending || branch.restrictions.dm}
                    className="grid place-items-center transition-transform active:scale-95 disabled:opacity-40 disabled:active:scale-100"
                  >
                    <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                      <PenTool className="size-[1.35rem]" />
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleActivityClick(branch.id, 'visit')}
                    disabled={createActivity.isPending || branch.restrictions.visit}
                    className="grid place-items-center transition-transform active:scale-95 disabled:opacity-40 disabled:active:scale-100"
                  >
                    <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                      <Footprints className="size-[1.35rem]" />
                    </div>
                  </button>
                </div>
              </div>
            </li>
          ))
        ) : (
          <li className="px-lg py-xl text-center text-sm text-gray-400">
            {data.branches && data.branches.length > 0 ? '条件に一致する枝客がいません' : '枝客がいません'}
          </li>
        )}
      </ul>
    </div>
  );
};
